import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import i18next from 'i18next';
import { createAuthClient } from '@munifocus/auth-client';
import { api, apiBaseUrl, setOnSessionExpired, type SessionEndReason } from '../lib/api-client';
import { isPasskeyCancellation, signInWithPasskey } from '../lib/webauthn-api';
import { forgetActiveScope } from './ScopeProvider';
import { forgetRecentFiles } from '../lib/recent-files';
import { queryClient } from '../lib/query-client';
import { clearIntakeDrafts } from '../lib/session-draft';

/**
 * Drop everything the previous account loaded or typed in this tab.
 *
 * Files, complaints, messages and permissions all sit in the query cache; left
 * there, the next person to sign in on a shared workstation would be shown the
 * previous officer's caseload until each query happened to refetch. The intake
 * draft carries a complainant's contact details for the same reason.
 */
function forgetSessionData(): void {
  queryClient.clear();
  clearIntakeDrafts();
}

/** Same key the language toggles write; a stored value is this device's explicit choice. */
const LOCALE_KEY = 'gov.locale';

/**
 * Open the portal in the officer's own language on sign-in. The account
 * carries it (it already drives their email), but the UI used to start in
 * English for everyone, so a francophone officer landed on an English screen
 * until they found the toggle. A choice made on this device still wins.
 */
function adoptAccountLanguage(user: AuthUser | null): void {
  const lang = user?.language;
  if (lang !== 'en' && lang !== 'fr') return;
  try {
    if (localStorage.getItem(LOCALE_KEY)) return;
  } catch {
    // Storage unavailable — fall through and follow the account.
  }
  if (!i18next.language?.startsWith(lang)) void i18next.changeLanguage(lang);
}

/** The authenticated officer, as returned by `/auth/login` and `/auth/me`. */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  language?: string;
  /**
   * Short-lived signed URL for the profile photo, or null for initials. It
   * expires, so it is never persisted client-side — every session response
   * carries a fresh one.
   */
  avatarUrl?: string | null;
  /** How this person is referred to, in their own words. Empty when unset. */
  pronouns?: string;
}

/** Why a sign-in attempt failed, so the UI can show the right message. */
export type AuthErrorKind = 'invalid' | 'rate-limited' | 'network' | 'seat-required' | 'unknown';

/** Typed login failure — `kind` drives which error copy the login screen shows. */
export class AuthError extends Error {
  constructor(readonly kind: AuthErrorKind) {
    super(kind);
    this.name = 'AuthError';
  }
}

/** Map a thrown request error to an {@link AuthError} by HTTP status. */
function classifyLoginError(err: unknown): AuthError {
  const response = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  const status = response?.status;
  // The password was right, but the account holds no enforcement seat: say so,
  // and who can fix it, rather than "check what you typed".
  if (response?.data?.code === 'seat_required') return new AuthError('seat-required');
  // 400 is the request being refused as malformed — in practice an email the
  // API will not accept — which is still "check what you typed", not an outage.
  if (status === 401 || status === 400) return new AuthError('invalid');
  if (status === 429) return new AuthError('rate-limited');
  // No `response` means the request never reached the server (offline, DNS,
  // CORS, timeout). A status we don't specifically handle (e.g. 5xx) is unknown.
  if (status === undefined) return new AuthError('network');
  return new AuthError('unknown');
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True until the initial `/auth/me` session probe resolves. */
  isLoading: boolean;
  /**
   * Why the last session ended, when the API said — `seat-required` when the
   * company administrator took the person's enforcement seat away. The sign-in
   * screen shows it; cleared by the next sign-in.
   */
  signedOutReason: SessionEndReason;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Sign in with an enrolled passkey. Errors are classified exactly like
   * `login`, except that a cancelled OS prompt is rethrown untouched so the
   * caller can stay silent rather than showing a failure the officer caused.
   */
  loginWithPasskey: () => Promise<void>;
  logout: () => Promise<void>;
  /** Request a reset link by email. Resolves regardless of whether the account exists. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Set a new password from a reset-link token. */
  resetPassword: (token: string, password: string) => Promise<void>;
  /** Replace the profile photo; resolves once the new one is in `user`. */
  uploadAvatar: (file: File) => Promise<void>;
  /** Set or clear your own pronouns. Only ever your own. */
  setPronouns: (pronouns: string) => Promise<void>;
  /** Drop the profile photo, falling back to initials. */
  removeAvatar: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// gov-api authenticates browser portals with httpOnly session cookies (the
// JWT guard reads the access cookie), so there's no token to hold client-side —
// `withCredentials` carries the session on every request. The source of truth
// for "am I signed in" is whether `/auth/me` resolves a user.
// `apiBaseUrl` is shared with the data client so both agree on the origin (and so
// a production build missing VITE_GOV_API_URL fails there rather than silently
// pointing this bundle at localhost).
const client = createAuthClient(apiBaseUrl);

function toUser(data: unknown): AuthUser | null {
  if (data && typeof data === 'object' && 'id' in data && 'email' in data) {
    return data as AuthUser;
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [signedOutReason, setSignedOutReason] = useState<SessionEndReason>(null);

  // Rehydrate an existing session from the cookie on first load, so a reload
  // doesn't bounce an already-signed-in officer back to /login. This probe goes
  // through the shared `api` instance, not the auth client: the access cookie
  // lives 15 min, so a reload after that — with a refresh cookie still good for
  // 7 days — must rotate the session rather than report "signed out". Only
  // `api` carries the refresh interceptor.
  useEffect(() => {
    let active = true;
    api
      .get('/auth/me')
      .then(({ data }) => {
        if (!active) return;
        const resolved = toUser(data);
        adoptAccountLanguage(resolved);
        setUser(resolved);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setUser(null);
        const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
        if (code === 'seat_required') setSignedOutReason('seat-required');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // A data request whose refresh also fails means the session is truly gone —
  // drop the user so ProtectedRoute bounces to /login, rather than leaving the
  // portal looking signed-in while every request 401s.
  useEffect(() => {
    setOnSessionExpired((reason) => {
      setUser(null);
      setSignedOutReason(reason);
      forgetSessionData();
    });
    return () => setOnSessionExpired(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    let result: { user?: unknown } | undefined;
    try {
      result = (await client.login(email, password)) as { user?: unknown } | undefined;
    } catch (err) {
      throw classifyLoginError(err);
    }
    const signedIn = toUser(result?.user);
    adoptAccountLanguage(signedIn);
    setSignedOutReason(null);
    setUser(signedIn);
  }, []);

  const loginWithPasskey = useCallback(async () => {
    let resolved: unknown;
    try {
      resolved = await signInWithPasskey();
    } catch (err) {
      // A dismissed prompt is not a failed sign-in; let the screen decide.
      if (isPasskeyCancellation(err)) throw err;
      throw classifyLoginError(err);
    }
    const signedIn = toUser(resolved);
    adoptAccountLanguage(signedIn);
    setUser(signedIn);
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.logout();
    } finally {
      setUser(null);
      // The active scope is remembered per browser, not per account. Leaving it
      // behind points the next account at the previous one's municipality.
      forgetActiveScope();
      forgetRecentFiles();
      forgetSessionData();
    }
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await client.requestPasswordReset(email);
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    await client.resetPassword(token, password);
  }, []);

  // The uploaded URL is applied straight to the held user rather than refetching
  // `/auth/me`: the POST already returns the freshly signed URL, and a refetch
  // would be a second round trip to learn what we were just told.
  const uploadAvatar = useCallback(async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<{ avatarUrl: string }>('/auth/me/avatar', form);
    setUser((current) => (current ? { ...current, avatarUrl: data.avatarUrl } : current));
  }, []);

  const removeAvatar = useCallback(async () => {
    await api.delete('/auth/me/avatar');
    setUser((current) => (current ? { ...current, avatarUrl: null } : current));
  }, []);

  // The stored value comes back rather than the typed one: the server trims
  // it, and rendering what was typed would put the whitespace back on screen a
  // moment after it was taken off.
  const setPronouns = useCallback(async (pronouns: string) => {
    const { data } = await api.patch<{ pronouns: string }>('/auth/me/pronouns', { pronouns });
    setUser((current) => (current ? { ...current, pronouns: data.pronouns } : current));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      signedOutReason,
      login,
      loginWithPasskey,
      logout,
      requestPasswordReset,
      resetPassword,
      uploadAvatar,
      removeAvatar,
      setPronouns,
    }),
    [
      user,
      isLoading,
      signedOutReason,
      login,
      loginWithPasskey,
      logout,
      requestPasswordReset,
      resetPassword,
      uploadAvatar,
      removeAvatar,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
